import { useState } from "react";
import { toast } from "sonner";
import api, { eur, formatApiError } from "@/lib/api";
import { sendMarketingReceipt } from "@/lib/marketingReceipt";
import { Btn } from "@/components/common";
import { Coffee, Beer, Gift, Package, Trash2 } from "lucide-react";

const ICON = { caffe: Coffee, birra: Beer, gadget: Gift, altro: Package };

export default function BarRoom({ r, items, canEditPaid, onChange }) {
  const [method, setMethod] = useState("contanti");
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); onChange(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const add = (it) => run(() => api.post("/bar/consumptions", { booking_id: r.booking_id, item_id: it.id, qty: 1 }));
  const del = (c) => {
    if (c.paid && !window.confirm(`${c.name} è già pagato: toglierlo riduce anche l'incasso registrato. Continuare?`)) return;
    run(() => api.delete(`/bar/consumptions/${c.id}`));
  };
  const unpaid = r.items.filter((c) => !c.paid).length;
  const pay = async () => {
    if (!window.confirm(`Segnare pagati ${eur(r.total)} per la camera ${r.room}? Si aprirà WhatsApp con la ricevuta PDF.`)) return;
    const win = window.open("about:blank", "_blank");
    setBusy(true);
    try {
      const { data } = await api.post("/bar/pay", { booking_id: r.booking_id, payment_method: method });
      toast.success("Pagato e registrato negli incassi");
      onChange();
      try { await sendMarketingReceipt(data.sale, win, true); }
      catch (e) { toast.error(`Ricevuta non inviata: ${formatApiError(e)}`); }
    } catch (e) { if (win) win.close(); toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`bar-room-${r.booking_id}`}>
      <div className="flex justify-between items-start">
        <div>
          <div className="font-semibold text-slate-900">{r.room} <span className="text-xs text-slate-400 font-normal">· {r.structure}</span>{r.in_house && <span className="ml-1 text-[10px] font-bold text-sky-700" data-testid={`bar-inhouse-${r.booking_id}`}>IN CASA</span>}</div>
          <div className="text-sm text-slate-600">{r.guest} · {r.checkin?.split("-").reverse().join("/")} → {r.checkout?.split("-").reverse().join("/")}</div>
        </div>
        <div className="text-right">
          <div className="text-lg font-bold text-sky-800" data-testid={`bar-total-${r.booking_id}`}>{eur(r.total)}</div>
          {r.paid_total > 0 && <div className="text-xs text-emerald-700" data-testid={`bar-paid-total-${r.booking_id}`}>pagato {eur(r.paid_total)}</div>}
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 my-3">
        {items.map((it) => {
          const I = ICON[it.category] || Package;
          return (
            <button key={it.id} disabled={busy} onClick={() => add(it)} data-testid={`bar-add-${r.booking_id}-${it.id}`}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-slate-200 text-sm hover:bg-sky-50 hover:border-sky-300 transition-colors">
              <I size={14} /> {it.name} <span className="text-slate-400">{eur(it.price)}</span>{it.stock != null && <span className={`text-[10px] font-semibold ${it.last_load && it.stock <= 0.2 * it.last_load ? "text-rose-600" : "text-slate-400"}`}>· {it.stock}</span>}
            </button>
          );
        })}
      </div>
      {r.items.length > 0 && (
        <>
          <ul className="text-sm divide-y divide-slate-100 mb-3">
            {r.items.map((c) => (
              <li key={c.id} className={`flex justify-between py-1 ${c.paid ? "text-slate-400" : ""}`} data-testid={`bar-cons-${c.id}`}>
                <span>{c.qty}× {c.name} <span className="text-xs text-slate-400">{c.created_at.slice(5, 16).replace("T", " ")}</span>{c.paid && <span className="ml-1 text-[10px] font-bold text-emerald-700">PAGATO</span>}</span>
                <span className="flex items-center gap-2">{eur(c.total)}{(!c.paid || canEditPaid) && <button onClick={() => del(c)} disabled={busy} data-testid={`bar-del-${c.id}`} className="text-rose-500 p-2 -m-1"><Trash2 size={14} /></button>}</span>
              </li>
            ))}
          </ul>
          {unpaid > 0 && <div className="flex gap-2 items-center">
            <select value={method} onChange={(e) => setMethod(e.target.value)} className="text-sm border border-slate-300 rounded-md px-2 py-1.5" data-testid={`bar-method-${r.booking_id}`}>
              <option value="contanti">Contanti</option>
              <option value="carta">Carta</option>
            </select>
            <Btn onClick={pay} disabled={busy} data-testid={`bar-pay-${r.booking_id}`}>Pagato</Btn>
          </div>}
        </>
      )}
    </div>
  );
}
